import {put, call, all} from 'redux-saga/effects';
import DeadlineRule from "../../entities/deadlineRule";
import * as Actions from "../actions/deadlineRuleActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DeadlineRuleAction = {
    deadlineRuleId: string;
    deadlineRule: DeadlineRule;
    deadlineRules: DeadlineRule[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneDeadlineRule(action: DeadlineRuleAction) {
	const session = tabSession.captureGeneration();
	try {
		const deadlineRule: DeadlineRule = yield call(api.findOne, 'deadlineRule', action.deadlineRuleId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DEADLINERULE_FETCH_ONE_SUCCESS, deadlineRuleId: action.deadlineRuleId, deadlineRule, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINERULE_FETCH_ONE_FAILURE, deadlineRuleId: action.deadlineRuleId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DeadlineRuleAction} action
 */
export function* saveDeadlineRule(action: DeadlineRuleAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DEADLINERULE_SAVE saga', action.deadlineRule?.id);

		if (action.deadlineRule === undefined || action.deadlineRule === null) {
			console.error('saveDeadlineRule received a null');
			throw new Error('saveDeadlineRule received a null');
		}

		if (!(action.deadlineRule instanceof DeadlineRule)) {
			action.deadlineRule = new DeadlineRule(action.deadlineRule);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.deadlineRule.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.deadlineRule);
			const addResult = yield call(idb.add, 'deadlineRule', action.deadlineRule);
			if (addResult.isErr()) {
				console.error(`⮂ saveDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertDeadlineRuleSave(action.deadlineRule, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.DEADLINERULE_SAVE_FAILURE, deadlineRule: action.deadlineRule, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.DEADLINERULE_SAVE, deadlineRule: action.deadlineRule, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertDeadlineRuleSave(action.deadlineRule, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.DEADLINERULE_SAVE_FAILURE, deadlineRule: action.deadlineRule, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {DeadlineRuleAction} action
 * @param {DeadlineRule[]} action.deadlineRules
 */
export function* saveManyDeadlineRules(action: DeadlineRuleAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DEADLINERULE_SAVE_MANY saga', action.deadlineRules.map(deadlineRule => deadlineRule.id).join(','));

        if (!Array.isArray(action.deadlineRules)) {
            console.error('saveManyDeadlineRules expected an array, received', action.deadlineRules);
            throw new Error('saveManyDeadlineRules expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveDeadlineRule), under these queue keys
		const dispatchedAddedOns: number[] = action.deadlineRules.map(deadlineRule => deadlineRule?.addedOn);
		const revertAll = () => action.deadlineRules.map((deadlineRule, i) =>
			put(Actions.revertDeadlineRuleSave(deadlineRule, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: DeadlineRule[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.deadlineRules.length; i++) {
				const deadlineRule = action.deadlineRules[i];
				deadlineRule.persisted = false;
				ensureOperationId(deadlineRule);
				toSave.push(deadlineRule);
			}

			const addResult = yield call(idb.add, 'deadlineRule', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyDeadlineRules: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.DEADLINERULE_SAVE_MANY_FAILURE, deadlineRules: action.deadlineRules, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.DEADLINERULE_SAVE_MANY, deadlineRules: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.DEADLINERULE_SAVE_MANY_FAILURE, deadlineRules: action.deadlineRules, status: 500, err});

			console.error(`Error saving ${action.deadlineRules.length} deadlineRules to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {DeadlineRuleAction} action
 */
export function* removeDeadlineRule(action: DeadlineRuleAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.deadlineRule === undefined || action.deadlineRule === null || !(action.deadlineRule instanceof DeadlineRule)) {
			console.error('removeDeadlineRule expected type DeadlineRule, received', action.deadlineRule);
			reportQueued(false);
			throw new Error('removeDeadlineRule expected type DeadlineRule');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.deadlineRule.status = 'D';
		action.deadlineRule.addedOn = newHRTimestamp();
		action.deadlineRule.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.deadlineRule);
		const addResult = yield call(idb.add, 'deadlineRule', action.deadlineRule);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.DEADLINERULE_REMOVE_FAILURE, deadlineRule: action.deadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINERULE_REMOVE, deadlineRule: action.deadlineRule});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINERULE_REMOVE_FAILURE, deadlineRule: action.deadlineRule, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this DeadlineRule.
	*
	* @param {DeadlineRuleAction} action
	*/
export function* restoreDeadlineRule(action: DeadlineRuleAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDeadlineRule expected getCurrentUserId() to return a value');
        return;
    }

	if (action.deadlineRule === undefined || action.deadlineRule === null) {
        console.error('restoreDeadlineRule expected a DeadlineRule, received null');
        throw new Error('restoreDeadlineRule expected a DeadlineRule, received null');
    }
    else if (!(action.deadlineRule instanceof DeadlineRule)) action.deadlineRule = new DeadlineRule(action.deadlineRule); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.deadlineRule.status = 'U';
		action.deadlineRule.addedOn = newHRTimestamp();
		action.deadlineRule.addedBy = userId;
		action.deadlineRule.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.deadlineRule);
		const addResult = yield call(idb.add, 'deadlineRule', action.deadlineRule);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DEADLINERULE_RESTORE_FAILURE, deadlineRule: action.deadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DEADLINERULE_RESTORE, deadlineRule: action.deadlineRule});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DEADLINERULE_RESTORE_FAILURE, deadlineRule: action.deadlineRule, status: 500, err});

		console.error('Error restoring from database', err);
	}
}